import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import QRCode from "qrcode";
import jsQR from "jsqr";
import { QrCode, ScanLine, Copy, Smartphone, Laptop, Building2, Lock, ClipboardPaste, Download, Images, Upload, RefreshCw } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { makeCode, showCode, sealSync, openSync, toFrames, parseFrame, recordSync, lastSync, lastPhotoSend, recordPhotoSend, snoozeSync, syncDue } from "../lib/qrSync";
import { download } from "../lib/reports";
import { today } from "../lib/format";
import { Section, Banner } from "./common";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Progress } from "./ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const SPEEDS = { Fast: 250, Slow: 600 };

function SendDialog({ onClose }) {
  const { state } = useStore();
  const [code] = useState(makeCode);
  const [text, setText] = useState(null);
  const [frames, setFrames] = useState([]);
  const [i, setI] = useState(0);
  const [speed, setSpeed] = useState("Fast");
  const canvas = useRef(null);
  useEffect(() => {
    sealSync(state, code).then((t) => { setText(t); setFrames(toFrames(t)); recordSync(); }).catch((e) => { console.error(e); toast.error("Couldn't prepare sync data on this browser"); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (frames.length < 2) return;
    const t = setInterval(() => setI((x) => (x + 1) % frames.length), SPEEDS[speed]);
    return () => clearInterval(t);
  }, [frames, speed]);
  useEffect(() => {
    if (canvas.current && frames[i]) QRCode.toCanvas(canvas.current, frames[i], { errorCorrectionLevel: "L", margin: 2, width: 300 });
  }, [frames, i]);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); toast.success("Sync text copied — paste it on the other device"); } catch { toast.error("Copy blocked — use Save as file instead"); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="sync-send-dialog">
        <DialogHeader>
          <DialogTitle>Send from this device</DialogTitle>
          <DialogDescription>On the other device open Settings → Sync devices → <b>Receive</b>, point its camera at this code until all pieces are collected, then type the sync code.</DialogDescription>
        </DialogHeader>
        <div className="grid place-items-center gap-3">
          {frames.length ? <canvas ref={canvas} className="rounded-xl border bg-white max-w-full" data-testid="sync-qr-canvas" /> : <div className="h-[300px] w-[300px] rounded-xl border bg-secondary grid place-items-center text-sm text-muted-foreground">Encrypting…</div>}
          <p className="text-xs text-muted-foreground" data-testid="sync-frame-counter">{frames.length ? `Code ${i + 1} of ${frames.length} · loops automatically` : ""}</p>
          <div className="flex gap-1">{Object.keys(SPEEDS).map((k) => <Button key={k} size="sm" variant={speed === k ? "default" : "outline"} className="h-7" onClick={() => setSpeed(k)} data-testid={`sync-speed-${k.toLowerCase()}`}>{k}</Button>)}</div>
          <div className="w-full rounded-xl bg-secondary p-3 text-center">
            <p className="text-xs text-muted-foreground flex items-center justify-center gap-1"><Lock className="h-3 w-3" /> Sync code — type it on the other device</p>
            <p className="font-display text-3xl font-bold tracking-widest num" data-testid="sync-code">{showCode(code)}</p>
          </div>
          <div className="flex flex-wrap justify-center gap-2 w-full">
            <Button variant="outline" size="sm" onClick={copy} disabled={!text} data-testid="sync-copy-text"><Copy className="h-3.5 w-3.5 mr-1" /> Copy as text</Button>
            <Button variant="outline" size="sm" onClick={() => download(text, `budget-sync-${today()}.txt`, "text/plain")} disabled={!text} data-testid="sync-save-file"><Download className="h-3.5 w-3.5 mr-1" /> Save as file</Button>
          </div>
          <p className="text-[11px] text-muted-foreground text-center">No camera? Copy the text or save the file, move it any way you like (it's encrypted), and paste it on the other device.</p>
          <PhotoSend code={code} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

async function shareOrDownload(text, name, title) {
  const file = new File([text], name, { type: "application/json" });
  if (navigator.canShare?.({ files: [file] }) && window.matchMedia("(pointer: coarse)").matches) {
    try { await navigator.share({ files: [file], title }); return "shared"; } catch (e) { if (e.name === "AbortError") return null; }
  }
  download(text, name, "application/json");
  return "saved";
}

function PhotoSend({ code }) {
  const { state, exportSyncPhotos, getReceipt } = useStore();
  const [since, setSince] = useState(lastPhotoSend);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(0);
  const metas = [...state.transactions.flatMap((t) => t.receipts || []), ...(state.receiptInbox || [])].filter((m) => !since || String(m.addedAt) > since);
  const sig = metas.map((m) => m.id).join();
  // Only count photos whose file is actually on this device
  useEffect(() => {
    let live = true;
    Promise.all(metas.map((m) => getReceipt(m).then((b) => !!b).catch(() => false))).then((r) => live && setPending(r.filter(Boolean).length));
    return () => { live = false; };
  }, [sig]); // eslint-disable-line react-hooks/exhaustive-deps
  const go = async () => {
    setBusy(true);
    try {
      const at = new Date().toISOString();
      const { text, count } = await exportSyncPhotos(since, code);
      if (!text) { toast.info("No new receipt photos to send"); return; }
      const how = await shareOrDownload(text, `budget-sync-photos-${today()}-${count}.json`, `${count} receipt photo(s)`);
      if (!how) return;
      recordPhotoSend(at);
      setSince(at);
      toast.success(`${count} photo${count === 1 ? "" : "s"} ${how === "shared" ? "ready to send" : "saved"} — add it on the other device after receiving the data, with the same code`);
    } catch (e) {
      console.error(e);
      toast.error("Couldn't create the photos file");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="w-full rounded-xl border p-3 text-sm" data-testid="sync-photo-send">
      <p className="flex items-center gap-2 font-medium"><Images className="h-4 w-4 text-primary" /> Receipt photos</p>
      <p className="text-xs text-muted-foreground mt-1" data-testid="sync-photo-pending">{pending ? `${pending} photo${pending === 1 ? "" : "s"} added since ${since ? `your last photo send (${new Date(since).toLocaleDateString()})` : "you started"}.` : "No new photos since your last photo send."} Photos are too big for QR codes, so they travel as a separate encrypted file locked with the same code.</p>
      <Button size="sm" variant="outline" className="mt-2" onClick={go} disabled={busy || !pending} data-testid="sync-photo-send-button"><Images className="h-3.5 w-3.5 mr-1" /> {busy ? "Preparing…" : pending ? `Send ${pending} photo${pending === 1 ? "" : "s"} as a file` : "Send photos as a file"}</Button>
    </div>
  );
}

export function PhotoImport({ code: preset = "" }) {
  const { importSyncPhotos } = useStore();
  const [code, setCode] = useState(preset);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const run = async (file) => {
    if (!code.trim()) return toast.error("Type the sync code first");
    setBusy(true);
    try {
      const { added, waiting, already } = await importSyncPhotos(await file.text(), code);
      toast.success(`${added} photo${added === 1 ? "" : "s"} added${already ? ` · ${already} already here` : ""}${waiting ? ` · ${waiting} belong to transactions not on this device yet — sync the data first, then add this file again` : ""}`);
    } catch (e) {
      toast.error(e.message || "Couldn't add those photos");
    }
    setBusy(false);
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="sync-photo-import">
      {!preset && <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Sync code" className="max-w-[160px] h-9 uppercase tracking-widest" data-testid="sync-photo-code-input" />}
      <Button size="sm" variant="outline" onClick={() => ref.current?.click()} disabled={busy} data-testid="sync-photo-import-button"><Upload className="h-3.5 w-3.5 mr-1" /> {busy ? "Adding…" : "Add photos file"}</Button>
      <input ref={ref} type="file" accept="application/json,.json" className="hidden" onChange={(e) => { e.target.files?.[0] && run(e.target.files[0]); e.target.value = ""; }} data-testid="sync-photo-file-input" />
    </div>
  );
}

function useScanner(active, onFrame) {
  const video = useRef(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!active) return;
    let stream, raf, stop = false;
    const c = document.createElement("canvas");
    const g = c.getContext("2d", { willReadFrequently: true });
    const tick = () => {
      if (stop) return;
      const v = video.current;
      if (v && v.readyState >= 2 && v.videoWidth) {
        const s = Math.min(1, 720 / v.videoWidth);
        c.width = Math.round(v.videoWidth * s);
        c.height = Math.round(v.videoHeight * s);
        g.drawImage(v, 0, 0, c.width, c.height);
        const hit = jsQR(g.getImageData(0, 0, c.width, c.height).data, c.width, c.height, { inversionAttempts: "dontInvert" });
        if (hit?.data) onFrame(hit.data);
      }
      raf = requestAnimationFrame(tick);
    };
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: "environment" }, audio: false })
      .then((s) => {
        stream = s;
        if (stop) return s.getTracks().forEach((t) => t.stop());
        video.current.srcObject = s;
        video.current.play().catch(() => {});
        tick();
      })
      .catch(() => setError("Camera unavailable — allow camera access, or paste the sync text instead."));
    if (!navigator.mediaDevices) setError("This browser can't use the camera here — paste the sync text instead.");
    return () => { stop = true; cancelAnimationFrame(raf); stream?.getTracks().forEach((t) => t.stop()); };
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps
  return { video, error };
}

function Collector({ onText }) {
  const [got, setGot] = useState({ id: null, n: 0, parts: {} });
  const ref = useRef(got);
  const done = useRef(false);
  const { video, error } = useScanner(true, (s) => {
    const f = parseFrame(s);
    if (!f || done.current) return;
    const cur = ref.current.id === f.id ? ref.current : { id: f.id, n: f.n, parts: {} };
    if (cur.parts[f.i] === undefined) {
      const next = { ...cur, parts: { ...cur.parts, [f.i]: f.chunk } };
      ref.current = next;
      setGot(next);
      if (Object.keys(next.parts).length === next.n) {
        done.current = true;
        onText(Array.from({ length: next.n }, (_, k) => next.parts[k]).join(""));
      }
    }
  });
  const have = Object.keys(got.parts).length;
  return (
    <div className="space-y-2" data-testid="sync-scanner">
      {error ? <p className="text-sm text-expense" data-testid="sync-camera-error">{error}</p> : <video ref={video} playsInline muted className="w-full rounded-xl bg-black aspect-video object-cover" />}
      <Progress value={got.n ? (have / got.n) * 100 : 0} />
      <p className="text-xs text-muted-foreground" data-testid="sync-scan-progress">{got.n ? `Collected ${have} of ${got.n} codes — keep the camera steady` : "Point the camera at the moving QR code on the other device"}</p>
    </div>
  );
}

function ReceiveDialog({ onClose }) {
  const { state, applySync } = useStore();
  const [mode, setMode] = useState("scan");
  const [text, setText] = useState("");
  const [code, setCode] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [applied, setApplied] = useState(false);
  const unlock = async (t = text) => {
    if (!code.trim()) return toast.error("Type the sync code shown on the other device");
    setBusy(true);
    try { setData(await openSync(t, code)); } catch (e) { toast.error(e.message || "Couldn't open that sync data"); }
    setBusy(false);
  };
  const apply = (how) => {
    if (how === "replace" && !window.confirm("Replace everything on this device with the other device's data? This device keeps its settings and receipt photos. You can Undo right after.")) return;
    applySync(data, how);
    recordSync();
    toast.success(how === "replace" ? "This device now matches the other device" : `Merged ${(data.transactions || []).length} transactions from the other device`);
    setApplied(true);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="sync-receive-dialog">
        <DialogHeader>
          <DialogTitle>Receive on this device</DialogTitle>
          <DialogDescription>On the other device tap <b>Send</b>, then scan its QR code here.</DialogDescription>
        </DialogHeader>
        {applied ? (
          <div className="space-y-3" data-testid="sync-applied">
            <p className="text-sm text-income font-medium">Data synced.</p>
            <p className="text-sm">Did the other device also save a <b>photos file</b>? Add it now — the same code unlocks it.</p>
            <PhotoImport code={code} />
            <Button className="w-full" onClick={onClose} data-testid="sync-done-button">Done</Button>
          </div>
        ) : data ? (
          <div className="space-y-3" data-testid="sync-preview">
            <div className="rounded-xl bg-secondary p-3 text-sm">
              <p>From the other device (sent {new Date(data.sentAt).toLocaleString()}):</p>
              <p className="font-medium" data-testid="sync-preview-counts">{(data.transactions || []).length} transactions · {(data.bills || []).length} bills · {(data.debts || []).length} debts · {(data.goals || []).length} goals · {(data.mileage || []).length} trips</p>
              <p className="text-xs text-muted-foreground mt-1">This device has {state.transactions.length} transactions.</p>
            </div>
            <Button className="w-full" onClick={() => apply("merge")} data-testid="sync-apply-merge">Merge into this device</Button>
            <p className="text-[11px] text-muted-foreground -mt-2">Adds what's new; where both devices have the same item, the other device's version wins.</p>
            <Button variant="outline" className="w-full" onClick={() => apply("replace")} data-testid="sync-apply-replace">Replace this device's data</Button>
            <p className="text-[11px] text-muted-foreground -mt-2">Makes this device an exact copy — use it if you deleted things on the other device.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex gap-1">
              <Button size="sm" variant={mode === "scan" ? "default" : "outline"} onClick={() => setMode("scan")} data-testid="sync-mode-scan"><ScanLine className="h-3.5 w-3.5 mr-1" /> Scan QR</Button>
              <Button size="sm" variant={mode === "paste" ? "default" : "outline"} onClick={() => setMode("paste")} data-testid="sync-mode-paste"><ClipboardPaste className="h-3.5 w-3.5 mr-1" /> Paste text</Button>
            </div>
            {mode === "scan" ? (text ? <p className="text-sm text-income font-medium" data-testid="sync-scan-complete">All codes collected. Now type the sync code.</p> : <Collector onText={setText} />) : <Textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder="Paste the sync text or open the saved file and paste its contents" data-testid="sync-paste-input" />}
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Sync code (e.g. ABCD-2345)" className="text-center tracking-widest uppercase" data-testid="sync-code-input" />
            <Button className="w-full" onClick={() => unlock()} disabled={busy || !text.trim()} data-testid="sync-unlock-button">{busy ? "Unlocking…" : "Unlock"}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function DeviceSync() {
  const [open, setOpen] = useState(null);
  return (
    <Section title="Sync devices" subtitle="Keep your phone, home computer and office computer in step — straight from screen to camera, never through a server." action={<QrCode className="h-5 w-5 text-muted-foreground" />} testid="settings-sync">
      <div className="flex items-center gap-3 text-muted-foreground mb-4"><Smartphone className="h-5 w-5" /><span className="h-px w-8 bg-border" /><Laptop className="h-5 w-5" /><span className="h-px w-8 bg-border" /><Building2 className="h-5 w-5" /></div>
      <ol className="grid md:grid-cols-3 gap-3 text-sm mb-4">
        <li className="rounded-xl border p-3"><b className="text-primary">1. Send</b><br />On the device with the newest changes, tap <b>Send</b>. A moving QR code and an 8-letter code appear.</li>
        <li className="rounded-xl border p-3"><b className="text-primary">2. Receive</b><br />On the other device tap <b>Receive</b> and point its camera at the screen until every code is collected.</li>
        <li className="rounded-xl border p-3"><b className="text-primary">3. Unlock</b><br />Type the 8-letter code, then choose <b>Merge</b> or <b>Replace</b>. Repeat for your third device.</li>
      </ol>
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => setOpen("send")} data-testid="sync-send-button"><QrCode className="h-4 w-4 mr-1" /> Send from this device</Button>
        <Button variant="outline" onClick={() => setOpen("receive")} data-testid="sync-receive-button"><ScanLine className="h-4 w-4 mr-1" /> Receive on this device</Button>
      </div>
      <p className="text-xs text-muted-foreground mt-3" data-testid="sync-last">Data is encrypted (AES-256) with the one-time code before it's shown. Receipt photos travel as a separate encrypted file from the Send screen. {lastSync() ? `This device last synced ${new Date(lastSync()).toLocaleString()}.` : "This device hasn't synced yet."}</p>
      <div className="mt-3 rounded-xl border p-3 text-sm"><p className="font-medium mb-2 flex items-center gap-2"><Images className="h-4 w-4 text-primary" /> Got a photos file later?</p><PhotoImport /></div>
      {open === "send" && <SendDialog onClose={() => setOpen(null)} />}
      {open === "receive" && <ReceiveDialog onClose={() => setOpen(null)} />}
    </Section>
  );
}

export function SyncNudge({ className = "" }) {
  const [due, setDue] = useState(syncDue);
  const [open, setOpen] = useState(false);
  if (!due) return null;
  return (
    <div className={className}>
      <Banner icon={RefreshCw} testid="sync-nudge">
        <b>This device hasn't synced in over a week.</b> Send your latest changes to your other devices so they stay in step.
        <div className="flex flex-wrap gap-2 mt-2"><Button size="sm" onClick={() => setOpen(true)} data-testid="sync-nudge-send">Send now</Button><Button size="sm" variant="ghost" onClick={() => { snoozeSync(); setDue(false); }} data-testid="sync-nudge-snooze">Next week</Button></div>
      </Banner>
      {open && <SendDialog onClose={() => { setOpen(false); setDue(syncDue()); }} />}
    </div>
  );
}
